import { DataLayer } from '@by-miles/data-layer'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { genericDateErrorCopy } from '../../../../../../../constants'
import { useQuote } from '../../../../../../../hooks/useQuote'
import { useCarPageContext } from '../../../../../../../pages/CarPageProvider'
import { usePreQuoteContext } from '../../../../../../../pages/PageValidator'
import { DateErrorState, DateState } from '../../../../../utils/dateTypes'
import { handlePrefilledDate } from '../../../../../utils/handlePrefilledDate'
import { PurchaseDate } from '../PurchaseDate'

export const PurchaseDateContainer = () => {
  const quote = useQuote()

  const { handleUpdateCarDetails, updatedCarDetails } = useCarPageContext()
  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()

  const purchaseDate = dayjs(quote?.details?.vehicle?.purchase_date).format('MMM YYYY')
  const [confirmPurchaseDate, setConfirmPurchaseDate] = useState<boolean | undefined | null>(
    quote?.details?.vehicle?.purchase_date_boolean,
  )
  const [customPurchaseDate, setCustomPurchaseDate] = useState<DateState>(
    handlePrefilledDate(quote?.details?.vehicle?.purchase_date || ''),
  )

  const [dateError, setDateError] = useState<boolean>(false)
  const [customDateError, setCustomDateError] = useState('')

  const handleCheckDate = () => {
    if (
      !customPurchaseDate?.month ||
      !customPurchaseDate?.year ||
      customPurchaseDate?.month.toString()?.length > 2 ||
      customPurchaseDate?.year.toString()?.length !== 4
    ) {
      setCustomDateError(genericDateErrorCopy)
      return
    }
    let date = dayjs.utc(`${customPurchaseDate?.year}/${customPurchaseDate?.month}`)
    const manufacture_date = dayjs(quote?.details?.vehicle?.manufacture_date)
    if (date.isSame(manufacture_date, 'month')) {
      date = manufacture_date
    }
    if (date && date.isBefore(quote?.details?.vehicle?.manufacture_date)) {
      setCustomDateError("That's odd, your car hadn't even been built on this date. Please check and try again.")
    } else if (date && date.isAfter(dayjs().add(1, 'month'))) {
      setCustomDateError('Please select a date within 30 days of today')
    } else if (dateError) {
      setCustomDateError(genericDateErrorCopy)
    } else {
      const purchase_date = date.toISOString()
      handleUpdateCarDetails({ purchase_date })
    }
  }

  const handleConfirmPurchaseDate = (value: boolean) => {
    setConfirmPurchaseDate(value)
    DataLayer.customEvent({
      eventName: `car purchase ${value ? 'yes' : 'no'}`,
      section: 'car',
      eventType: 'form tracking',
    })
    handleUpdateCarDetails({ purchase_date_boolean: value })
  }

  const handleCustomPurchaseDateErrors = (errors: DateErrorState) => {
    if (errors.month || errors.year) {
      setDateError(true)
    }
  }

  useEffect(() => {
    updateValidInputs({ field: 'confirmPurchaseDate', value: typeof confirmPurchaseDate === 'boolean' })
    updateValidInputs({
      field: 'customPurchaseDate',
      value: !(confirmPurchaseDate === false && !customPurchaseDate),
    })
    if (confirmPurchaseDate && updatedCarDetails.purchase_date) {
      setCustomPurchaseDate(handlePrefilledDate(quote?.details?.vehicle?.purchase_date || ''))
      setCustomDateError('')
      handleUpdateCarDetails({ purchase_date: quote?.details?.vehicle?.purchase_date })
    }
  }, [confirmPurchaseDate])

  useEffect(() => {
    if (confirmPurchaseDate === false) {
      handleCheckDate()
    }
  }, [customPurchaseDate])

  useEffect(() => {
    updateValidInputs({
      field: 'customPurchaseDate',
      value: !(!confirmPurchaseDate && !!customDateError && !!dateError),
    })
  }, [customDateError, dateError])

  return (
    <PurchaseDate
      showErrorBanner={showErrorBanner}
      notAnsweredMessage={notAnsweredMessage}
      preExistingConfirmPurchaseDate={quote?.details?.vehicle?.purchase_date_boolean !== false}
      purchaseDate={purchaseDate}
      confirmPurchaseDate={confirmPurchaseDate}
      handleConfirmPurchaseDate={handleConfirmPurchaseDate}
      customPurchaseDate={customPurchaseDate}
      setCustomPurchaseDate={setCustomPurchaseDate}
      customPurchaseDateValid={validInputs?.customPurchaseDate}
      customDateError={customDateError}
      setCustomDateError={setCustomDateError}
      setDateError={setDateError}
      handleCheckDate={handleCheckDate}
      handleCustomPurchaseDateErrors={handleCustomPurchaseDateErrors}
      manufactureDate={quote?.details?.vehicle?.manufacture_date}
    />
  )
}
